import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft,
  Phone,
  Map as MapIcon,
  Plane,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PROGRESSION = [
  { from: "dispatched", to: "en_route", label: "Accept Run" },
  { from: "en_route", to: "chauffeur_arrived", label: "Arrived at Curb" },
  { from: "chauffeur_arrived", to: "passenger_loaded", label: "Passenger Loaded" },
  { from: "passenger_loaded", to: "completed", label: "Trip Completed" },
];

const STATUS_LABELS = {
  confirmed: "Confirmed",
  dispatched: "Dispatched",
  en_route: "En Route",
  chauffeur_arrived: "Arrived",
  passenger_loaded: "In Transit",
  completed: "Completed",
  cancelled: "Cancelled",
};

const TIMELINE_STAGES = [
  "dispatched",
  "en_route",
  "chauffeur_arrived",
  "passenger_loaded",
  "completed",
];

function fmtFull(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function DriverRunDetail() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [advancing, setAdvancing] = useState(false);
  const [clientPhone, setClientPhone] = useState(null);

  const fetchRun = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/bookings/${id}`);
      setBooking(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (user && user.role === "driver") fetchRun();
  }, [user, fetchRun]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center text-[#8E9AA6] text-xs uppercase tracking-[0.3em]">
        Loading…
      </div>
    );
  }

  if (!user) return <Navigate to="/driver/login" replace />;
  if (user.role !== "driver") return <Navigate to="/" replace />;

  const nextStep = booking && PROGRESSION.find((p) => p.from === booking.status);
  const isTerminal = booking && booking.status === "completed";

  const advance = async () => {
    if (!nextStep || advancing) return;
    setAdvancing(true);
    try {
      const { data } = await api.patch(`/bookings/${id}/status`, {
        status: nextStep.to,
      });
      setBooking(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setAdvancing(false);
    }
  };

  const stageIdx = booking ? TIMELINE_STAGES.indexOf(booking.status) : -1;

  return (
    <div className="min-h-screen bg-[#0B0B0C] text-white pb-16">
      <header className="sticky top-0 z-30 bg-[#0B0B0C]/95 backdrop-blur-xl border-b border-white/10 px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate("/driver")}
          data-testid="driver-run-back-btn"
          className="p-2 -ml-2 text-[#8E9AA6] hover:text-white transition-colors"
          aria-label="Back"
        >
          <ChevronLeft size={22} strokeWidth={1.5} />
        </button>
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#8E9AA6]">
            Run Detail
          </div>
          <div className="text-sm font-light">
            {booking ? booking.vehicle_category.replace(/_/g, " ") : "…"}
          </div>
        </div>
      </header>

      <main className="px-5 py-6 max-w-md mx-auto">
        {loading && (
          <div className="text-center text-[#8E9AA6] text-xs uppercase tracking-[0.3em] py-16">
            Loading run…
          </div>
        )}
        {error && <div className="text-sm text-red-400 mb-4" data-testid="driver-run-error">{error}</div>}

        {booking && (
          <>
            <div className="text-center mb-10">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#8E9AA6] mb-4">
                Current Status
              </div>
              <div
                data-testid="driver-run-status-big"
                className="font-serif text-4xl font-semibold tracking-tighter text-white"
              >
                {STATUS_LABELS[booking.status]}
              </div>
            </div>

            {/* Progress dots */}
            <div
              data-testid="driver-run-timeline"
              className="flex items-center justify-between mb-10 px-2"
            >
              {TIMELINE_STAGES.map((s, i) => {
                const done = stageIdx >= 0 && i <= stageIdx;
                const isCurrent = i === stageIdx;
                return (
                  <div key={s} className="flex-1 flex items-center">
                    <div
                      data-testid={`timeline-dot-${s}`}
                      className={`h-3 w-3 rounded-full border-2 transition-all ${
                        isCurrent
                          ? "bg-white border-white scale-125"
                          : done
                          ? "bg-[#E6E6E6] border-[#E6E6E6]"
                          : "bg-transparent border-white/20"
                      }`}
                    />
                    {i < TIMELINE_STAGES.length - 1 && (
                      <div
                        className={`flex-1 h-px ${
                          done && stageIdx > i ? "bg-[#E6E6E6]" : "bg-white/10"
                        }`}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Next-step CTA */}
            {nextStep ? (
              <button
                type="button"
                onClick={advance}
                disabled={advancing}
                data-testid="driver-advance-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-[#E6E6E6] disabled:opacity-50 px-8 py-5 transition-all duration-300 font-medium text-sm tracking-[0.25em] uppercase mb-10"
              >
                {advancing ? (
                  <>
                    <Loader2 size={16} strokeWidth={1.5} className="animate-spin" /> Updating…
                  </>
                ) : (
                  <>{nextStep.label}</>
                )}
              </button>
            ) : isTerminal ? (
              <div
                data-testid="driver-run-completed"
                className="w-full inline-flex items-center justify-center gap-2 bg-emerald-900/30 border border-emerald-400/40 text-emerald-300 px-8 py-5 mb-10 font-medium text-sm tracking-[0.25em] uppercase"
              >
                <CheckCircle2 size={18} strokeWidth={1.5} /> Trip Completed
              </div>
            ) : null}

            {/* Trip detail */}
            <div className="space-y-5 mb-8">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-1">
                  Pickup
                </div>
                <div className="text-base font-light">{fmtFull(booking.pickup_datetime)}</div>
                <div className="text-sm text-white mt-1">{booking.pickup_address}</div>
              </div>
              {booking.dropoff_address && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-1">
                    Drop-off
                  </div>
                  <div className="text-sm text-white">{booking.dropoff_address}</div>
                </div>
              )}
              {booking.duration_hours && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-1">
                    Duration
                  </div>
                  <div className="text-sm text-white">{booking.duration_hours} hrs</div>
                </div>
              )}
              <div className="flex gap-6 text-sm text-white">
                <span>
                  <span className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mr-2">
                    Pax
                  </span>
                  {booking.passengers}
                </span>
                <span>
                  <span className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mr-2">
                    Luggage
                  </span>
                  {booking.luggage}
                </span>
              </div>

              {booking.flight && booking.flight.valid && (
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] bg-white/5 border border-white/15 px-3 py-2 text-white">
                  <Plane size={12} strokeWidth={1.5} />
                  {booking.flight_number} · {booking.flight.status} · Gate {booking.flight.gate}
                </div>
              )}

              {booking.special_requests && (
                <div className="border-t border-white/10 pt-4">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-2">
                    Special Instructions
                  </div>
                  <p className="text-sm text-white italic font-light">
                    {booking.special_requests}
                  </p>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="grid grid-cols-2 gap-3 border-t border-white/10 pt-6">
              <a
                href="tel:+13475230763"
                data-testid="driver-call-client-btn"
                className="flex items-center justify-center gap-2 bg-transparent border border-white/30 text-white hover:bg-white hover:text-black px-4 py-4 transition-all duration-300 text-xs tracking-[0.25em] uppercase"
              >
                <Phone size={14} strokeWidth={1.5} /> Call Client
              </a>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                  booking.pickup_address
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="driver-navigate-btn"
                className="flex items-center justify-center gap-2 bg-transparent border border-white/30 text-white hover:bg-white hover:text-black px-4 py-4 transition-all duration-300 text-xs tracking-[0.25em] uppercase"
              >
                <MapIcon size={14} strokeWidth={1.5} /> Navigate
              </a>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
